import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../ui/Button';

/**
 * SignaturePad — canvas-based digital signature input.
 *
 * Pointer events cover mouse + touch + pen. Produces a PNG dataURL via
 * `canvas.toDataURL('image/png')` ready to be stored on a SignatureRecord.
 *
 * UX: tall responsive canvas, "Clear" button, "Empty"/"Captured" state
 * indicator. Disabled state for read-only contexts.
 */

export interface SignaturePadProps {
  /** Existing PNG dataURL — read-only display. */
  value?: string;
  /** Called with the new dataURL on mouse/pointer up. */
  onChange?: (dataUrl: string | null) => void;
  /** When true, canvas is locked. */
  disabled?: boolean;
  /** Canvas height in CSS px. Default 160. */
  height?: number;
}

const SignaturePad: React.FC<SignaturePadProps> = ({
  value, onChange, disabled = false, height = 160,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const [hasInk, setHasInk] = useState<boolean>(!!value);
  const [pixelRatio, setPixelRatio] = useState<number>(1);

  // Initial render: set canvas size to match container * devicePixelRatio
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    setPixelRatio(ratio);
    const rect = canvas.getBoundingClientRect();
    canvas.width  = Math.round(rect.width  * ratio);
    canvas.height = Math.round(rect.height * ratio);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineCap   = 'round';
    ctx.lineJoin  = 'round';
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#1c1917'; // stone-900

    // If we have an initial value, render it
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = value;
      setHasInk(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-render value on prop change (e.g. external clear)
  useEffect(() => {
    setHasInk(!!value);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (!value) {
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
    }
  }, [value]);

  // ─── Pointer handlers ─────────────────────────────────────────

  const pointFromEvent = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    e.preventDefault();
    const canvas = canvasRef.current!;
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    lastPointRef.current = pointFromEvent(e);
  }, [disabled]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || disabled) return;
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const p = pointFromEvent(e);
    const last = lastPointRef.current;
    if (last) {
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    }
    lastPointRef.current = p;
    setHasInk(true);
  }, [disabled]);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    const canvas = canvasRef.current!;
    try { canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (hasInk && onChange) {
      const dataUrl = canvas.toDataURL('image/png');
      onChange(dataUrl);
    }
  }, [hasInk, onChange]);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    setHasInk(false);
    onChange?.(null);
  }, [onChange]);

  return (
    <div className="space-y-2">
      <div
        className={[
          'relative w-full rounded-xl border-2 border-dashed bg-white overflow-hidden',
          disabled
            ? 'border-stone-200 cursor-not-allowed'
            : hasInk
              ? 'border-stone-400'
              : 'border-stone-300 hover:border-stone-400',
        ].join(' ')}
        style={{ height }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          className="w-full h-full"
          style={{ touchAction: 'none', cursor: disabled ? 'not-allowed' : 'crosshair' }}
          aria-label="Signature canvas"
          role="img"
        />
        {!hasInk && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-stone-400 text-sm">
            ✍ Draw your signature above
          </div>
        )}
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className={hasInk ? 'text-emerald-700' : 'text-stone-500'}>
          {hasInk ? '✓ Signature captured' : 'Empty — draw to sign'}
        </span>
        {!disabled && hasInk && (
          <Button variant="tertiary" icon="restart_alt" onClick={clear}>Clear</Button>
        )}
      </div>
      {pixelRatio > 1 && process.env.NODE_ENV === 'development' && (
        <div className="text-[10px] text-stone-400">DPI: {pixelRatio}×</div>
      )}
    </div>
  );
};

export default SignaturePad;
